import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";

const inputCls = "w-full mt-2 px-3 py-3 border border-zinc-300 focus:outline-none focus:border-[#0033FF] focus:ring-2 focus:ring-[#0033FF]/20 text-base";
const btnCls = "w-full bg-[#0033FF] text-white px-4 py-3.5 text-sm font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 flex items-center justify-center gap-2";

const Shell = ({ eyebrow, title, children }) => (
  <div className="min-h-screen flex items-center justify-center bg-white p-8">
    <div className="w-full max-w-md">
      <div className="flex items-center gap-2 mb-10"><Logo size={32} /><div className="text-lg font-black tracking-tighter">VIZIONCAST</div></div>
      <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold mb-3">{eyebrow}</div>
      <h1 className="text-4xl font-black tracking-tighter mb-6">{title}</h1>
      {children}
      <div className="mt-8 text-sm text-zinc-600"><Link to="/login" data-testid="back-to-login" className="font-bold text-zinc-900 underline underline-offset-4">Back to sign in</Link></div>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Shell eyebrow="Forgot password" title={sent ? "Check your inbox." : "Reset your password."}>
      {sent ? (
        <div className="flex gap-3 text-sm text-zinc-600" data-testid="forgot-sent">
          <MailCheck className="w-5 h-5 text-[#0033FF] shrink-0" />
          <p>If <span className="font-bold text-zinc-900">{email}</span> has a VizionCast account, a reset link is on its way. It works once and expires in 1 hour.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <p className="text-sm text-zinc-600 -mt-2">Enter your account email and we'll send you a link to choose a new password.</p>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Email</label>
            <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
          </div>
          {err && <div data-testid="forgot-error" className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1">{err}</div>}
          <button data-testid="forgot-submit-btn" disabled={busy} className={btnCls}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true); setErr("");
    try { await api.post("/auth/reset-password", { token, password: pw }); nav("/login?reset=1"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Shell eyebrow="New password" title="Choose a new password.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">New password</label>
          <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Confirm password</label>
          <input data-testid="reset-password-confirm-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required className={inputCls} />
        </div>
        {err && <div data-testid="reset-error" className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1">{err}</div>}
        <button data-testid="reset-submit-btn" disabled={busy} className={btnCls}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save new password <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </Shell>
  );
}
